import { h } from 'preact';
import Store from 'store/store';
import { sortObjectByOrder } from 'utils/utils';
import { getIsSinglePageIframed } from 'utils/bolt/client';
import { setAppData } from 'utils/dynamicData';
import { getPageIdFromURL } from 'utils/bolt';
import * as tracking from 'store/bolt/trackingV2';
import { findInVNode } from 'utils/vnode';
import { previewMode } from 'utils/dynamicData';
import { waitForOneTrust } from 'utils/gdpr/oneTrust';
import { pages, sections } from 'pages/bolt_pages';

import * as BaseComp from 'base/bolt/boltApp.js';

/**
 * Update sections array parents to match the actual main page.
 * This handles the case where boltPreviewPanel is set as the main page via CMS,
 * but sections are built with parent: 'boltHome'.
 */
function updateSectionParents(appData) {
	if (appData.pages?.boltPreviewPanel && !appData.pages?.boltHome) {
		sections.forEach(section => {
			if (section.parent === 'boltHome') {
				section.parent = 'boltPreviewPanel';
			}
		});
	}
}

async function autoAcceptOneTrust() {
	await waitForOneTrust();
	window.OneTrust?.AllowAll();
}

export default class BoltApp extends BaseComp.default {
	constructor(props) {
		super(props);

		this.state.dynamicDataStatus = 'loading';

		this.loadDynamicData();
	}

	componentDidMount() {
		super.componentDidMount();
		if (getIsSinglePageIframed()) {
			document.body.classList.add('singlePageIframed');
		}
		if (previewMode) {
			autoAcceptOneTrust();
		}
	}

	runOnLoad() {
		// Ensure dynamic data is loaded before running onLoad logic
		if (this.state.dynamicDataStatus !== 'loaded') return;
		super.runOnLoad();
	}

	async loadDynamicData() {
		if (typeof window === 'undefined') return;
		if (__IS_LOCAL__ && location.search.includes('ignore-cms')) {
			this.setState({ dynamicDataStatus: 'loaded' });
			return;
		}
		try {
			const initialAppData = Store.get().appData?.toJS() || {};

			const newAppData = await setAppData(initialAppData);

			// Update section parents based on the actual main page from CMS data
			updateSectionParents(newAppData);

			let activePage = getPageIdFromURL(newAppData.pages) || Store.get().activePage;
			Store.get().set({ appData: newAppData, activePage }).now();

			this.setState({ dynamicDataStatus: 'loaded', isComponentLoaded: this.state.isComponentLoaded || !pages[activePage] });
			this.getBgData();
			this.runOnLoad();
		} catch (err) {
			console.error('[BOLT APP] Error loading dynamic data:', err);
			this.setState({ dynamicDataStatus: 'error' });
		}
	}

	getBgData() {
		const store = this.state.appState;
		const pagesData = store.appData?.pages || {};
		const activePage = store.activePage || '';

		let relevantPageData = pagesData[activePage];
		const sectionOf = relevantPageData?.section === true ? 'boltHome' : relevantPageData?.section;
		if (sectionOf) {
			relevantPageData = pagesData[sectionOf];
			// home section without an actual boltHome (e.g. previewPanel): use the background of the top section instead
			if (sectionOf === 'boltHome' && !relevantPageData) {
				relevantPageData = sortObjectByOrder(pagesData).filter(page => {
					return (sectionOf === 'boltHome' && page?.section === true) || page?.section === sectionOf;
				})[0];
			}
		}
		if (!relevantPageData) return;

		this.backgroundData = relevantPageData?.data?.bg || {};
	}

	renderBgVideo() {
		const base = super.renderBgVideo();
		if (!base) return null;

		// Remove alt and aria-label from the video element due to SEO requirements
		const video = findInVNode(base, 'video');
		if (video?.attributes?.ariaLabel) delete video.attributes.ariaLabel;
		if (video?.attributes?.alt) delete video.attributes.alt;
		return base;
	}

	render(props, state) {
		if (state.dynamicDataStatus === 'loading') {
			// TODO: loader?
			return <div />;
		}
		return super.render(props, state);
	}
}
